import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { endpoints } from "../../api/endpoints";
import type { Authority, Role } from "../../types/user";

export function useAuthorities() {
  return useQuery({
    queryKey: ["authorities"],
    queryFn: async () => {
      const { data } = await api.get<Authority[]>(endpoints.authorities.list());
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useRoleList() {
  return useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const { data } = await api.get<Role[]>(endpoints.roles.list());
      return data;
    },
  });
}

function useInvalidateRoles() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: ["roles"] });
}

export interface RolePayload {
  id: number;
  name: string;
  authorities: { id: number }[];
}

export function useSaveRole() {
  const invalidate = useInvalidateRoles();
  return useMutation({
    mutationFn: async (payload: RolePayload) => {
      const { data } = await api.put(endpoints.roles.update(String(payload.id)), payload);
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useCreateRole() {
  const invalidate = useInvalidateRoles();
  return useMutation({
    mutationFn: async (name: string) => {
      const { data } = await api.post(endpoints.roles.create(), { name, authorities: [] });
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteRole() {
  const invalidate = useInvalidateRoles();
  return useMutation({
    mutationFn: async (roleId: number) => {
      const { data } = await api.delete(endpoints.roles.remove(String(roleId)));
      return data;
    },
    onSuccess: invalidate,
  });
}
